import { AxiosRequestConfig } from 'axios';
import { collectionSetupAxiosInstance } from '../../axios/collectionSetupAxios';

const blobConfig: AxiosRequestConfig = { responseType: 'blob' };

export interface STRRule {
  id?: string;
  ruleCode: string;
  ruleName: string;
  isEnabled: boolean;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  defaultSeverity?: 'LOW' | 'MEDIUM' | 'HIGH';
  configSchema?: any;
  thresholds: STRRuleThreshold[];
  createdAt?: string;
  updatedAt?: string;
}

export interface STRRuleThreshold {
  id?: string;
  ruleCode?: string;
  thresholdKey: string;
  thresholdValue: number;
  dataType: 'DECIMAL' | 'INTEGER' | 'BOOLEAN' | 'STRING';
  severity?: 'LOW' | 'MEDIUM' | 'HIGH';
  isActive?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface STRAlert {
  id: string;
  agreementId: string;
  ruleCode: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'ACTIVE' | 'RESOLVED' | 'DISMISSED' | 'HIDDEN';
  alertMessage: string;
  triggerData: any;
  isHidden: boolean;
  hiddenBy?: string;
  hiddenAt?: string;
  hiddenReason?: string;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  accountDetails?: {
    agreementId: string;
    agreementNo?: string;
    applicantFirstName: string;
    applicantEmail: string;
  };
}

export interface STRAccountMonitoring {
  id: string;
  agreementId: string;
  isMonitoringEnabled: boolean;
  disabledRules: string[];
  reason?: string;
  disabledBy?: string;
  disabledAt?: string;
  enabledBy?: string;
  enabledAt?: string;
  lastUpdated: string;
}

export interface STRDashboardStats {
  totalAlerts: number;
  activeAlerts: number;
  resolvedAlerts: number;
  hiddenAlerts: number;
  alertsByRule: Array<{ ruleCode: string; count: number }>;
  alertsBySeverity: Array<{ severity: string; count: number }>;
  alertsByDay: Array<{ date: string; count: number }>;
  totalMonitoredAccounts: number;
  disabledAccounts: number;
  averageProcessingTime: number;
  topAlertingAccounts: Array<{
    agreementId: string;
    customerName: string;
    alertCount: number;
    lastAlertDate: string;
  }>;
}

export interface STRDailySummary {
  date: string;
  totalPayments: number;
  totalAlerts: number;
  alertsByRule: Record<string, number>;
  alertsBySeverity: Record<string, number>;
  topAlertingAccounts: Array<{
    agreementId: string;
    customerName: string;
    alertCount: number;
  }>;
  systemPerformance: {
    averageProcessingTime: number;
    totalProcessingTime: number;
    errorRate: number;
  };
}

export interface STRAccountFlags {
  agreementId: string;
  customerName: string;
  email: string;
  phone: string;
  monitoringStatus: {
    isEnabled: boolean;
    disabledRules: string[];
    reason?: string;
    lastUpdated: string;
  };
  alertsSummary: {
    totalAlerts: number;
    alertsByRule: Record<string, number>;
    alertsBySeverity: Record<string, number>;
    recentAlerts: STRAlert[];
  };
  auditLog: Array<{
    action: string;
    performedBy: string;
    performedAt: string;
    reason: string;
    previousState: any;
    newState: any;
  }>;
}

export interface STRExcelExportParams {
  dateFrom: string;
  dateTo: string;
  agreementId?: string;
  ruleCode?: string;
  includeHidden?: boolean;
  includeResolved?: boolean;
}

export interface STREmailParams {
  recipients: string[];
  subject: string;
  reportType: 'alerts' | 'monitoring-audit' | 'rule-executions' | 'dashboard-summary';
  dateFrom?: string;
  dateTo?: string;
  filters?: {
    status?: 'ACTIVE' | 'RESOLVED' | 'HIDDEN';
    severity?: 'LOW' | 'MEDIUM' | 'HIGH';
    ruleCode?: string;
    agreementId?: string;
    actionType?: string;
    result?: 'NO_ALERT' | 'ALERT_CREATED' | 'ERROR';
  };
  includeHidden?: boolean;
  includeResolved?: boolean;
  customMessage?: string;
}

const getDashboardStats = async (requestData?: { dateFrom?: string; dateTo?: string }) => {
  const params = new URLSearchParams();
  if (requestData?.dateFrom) params.append('dateFrom', requestData.dateFrom);
  if (requestData?.dateTo) params.append('dateTo', requestData.dateTo);

  const url = `/redCash/api/v1.0/str-dashboard/stats?${params}`;
  const res = await collectionSetupAxiosInstance.get(url);
  // Extract the data field from the backend response
  return res.data.data || {};
};

const getDailySummary = async (requestData: { date: string }) => {
  const url = `/redCash/api/v1.0/str-summary/daily?date=${requestData.date}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data;
};

const getWeeklySummary = async (requestData: { startDate: string }) => {
  const url = `/redCash/api/v1.0/str-summary/weekly?startDate=${requestData.startDate}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data;
};

const getAllRules = async () => {
  const url = `/redCash/api/v1.0/str-config/rules`;
  const res = await collectionSetupAxiosInstance.get(url);
  // Extract the data field from the backend response
  return res.data.data || [];
};

const getRule = async (requestData: { ruleCode: string }) => {
  const url = `/redCash/api/v1.0/str-config/rules/${requestData.ruleCode}`;
  const res = await collectionSetupAxiosInstance.get(url);
  // Extract the data field from the backend response
  return res.data.data || res.data;
};

const updateRuleThresholds = async (requestData: {
  ruleCode: string;
  thresholds: Record<string, any>;
}) => {
  const url = `/redCash/api/v1.0/str-config/rules/${requestData.ruleCode}/thresholds`;
  const res = await collectionSetupAxiosInstance.put(url, { thresholds: requestData.thresholds });
  return res.data;
};

const toggleRule = async (requestData: { ruleCode: string; isEnabled: boolean }) => {
  const url = `/redCash/api/v1.0/str-config/rules/${requestData.ruleCode}/toggle`;
  const res = await collectionSetupAxiosInstance.put(url, { isEnabled: requestData.isEnabled });
  return res.data;
};

const getAlerts = async (requestData: {
  agreementId?: string;
  ruleCode?: string;
  severity?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}) => {
  const queryParams = new URLSearchParams();
  Object.entries(requestData).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value.toString());
  });

  const url = `/redCash/api/v1.0/str-alerts?${queryParams}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data;
};

const hideAlert = async (requestData: { alertId: string; reason: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/${requestData.alertId}/hide`;
  const res = await collectionSetupAxiosInstance.post(url, { reason: requestData.reason });
  return res.data;
};

const showAlert = async (requestData: { alertId: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/${requestData.alertId}/show`;
  const res = await collectionSetupAxiosInstance.post(url);
  return res.data;
};

const resolveAlert = async (requestData: { alertId: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/${requestData.alertId}/resolve`;
  const res = await collectionSetupAxiosInstance.post(url);
  return res.data;
};

const bulkHideAlerts = async (requestData: { alertIds: string[]; reason: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/bulk-hide`;
  const res = await collectionSetupAxiosInstance.post(url, {
    alertIds: requestData.alertIds,
    reason: requestData.reason,
  });
  return res.data;
};

const hideAllAccountAlerts = async (requestData: { agreementId: string; reason: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/account/${requestData.agreementId}/hide-all`;
  const res = await collectionSetupAxiosInstance.post(url, { reason: requestData.reason });
  return res.data;
};

const hideRuleAlertsForAccount = async (requestData: {
  agreementId: string;
  ruleCode: string;
  reason: string;
}) => {
  const url = `/redCash/api/v1.0/str-alerts/account/${requestData.agreementId}/rule/${requestData.ruleCode}/hide`;
  const res = await collectionSetupAxiosInstance.post(url, { reason: requestData.reason });
  return res.data;
};

const getAlertStatistics = async (requestData: { dateFrom: string; dateTo: string }) => {
  const url = `/redCash/api/v1.0/str-alerts/statistics?dateFrom=${requestData.dateFrom}&dateTo=${requestData.dateTo}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data;
};

const getAccountMonitoringStatus = async (requestData: { agreementId: string }) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data.data;
};

const searchAccountMonitoringByAgreementNo = async (requestData: { agreementNo: string }) => {
  const url = `/redCash/api/v1.0/str-monitoring/search?agreementNo=${requestData.agreementNo}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data.data;
};

const disableAccountMonitoring = async (requestData: { agreementId: string; reason: string }) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}/disable`;
  const res = await collectionSetupAxiosInstance.post(url, { reason: requestData.reason });
  return res.data.data;
};

const enableAccountMonitoring = async (requestData: { agreementId: string; reason: string }) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}/enable`;
  const res = await collectionSetupAxiosInstance.post(url, { reason: requestData.reason });
  return res.data.data;
};

const disableAccountRules = async (requestData: {
  agreementId: string;
  rules: string[];
  reason: string;
}) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}/disable-rules`;
  const res = await collectionSetupAxiosInstance.post(url, {
    rules: requestData.rules,
    reason: requestData.reason,
  });
  return res.data.data;
};

const enableAccountRules = async (requestData: {
  agreementId: string;
  rules: string[];
  reason: string;
}) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}/enable-rules`;
  const res = await collectionSetupAxiosInstance.post(url, {
    rules: requestData.rules,
    reason: requestData.reason,
  });
  return res.data.data;
};

const unflagAccount = async (requestData: {
  agreementId: string;
  reason: string;
  rulesToEnable?: string[];
}) => {
  const url = `/redCash/api/v1.0/str-monitoring/account/${requestData.agreementId}/unflag`;
  const res = await collectionSetupAxiosInstance.post(url, {
    reason: requestData.reason,
    rulesToEnable: requestData.rulesToEnable,
  });
  return res.data.data;
};

const getAccountFlags = async (requestData: { agreementId: string }) => {
  const url = `/redCash/api/v1.0/str-account-flags/${requestData.agreementId}`;
  const res = await collectionSetupAxiosInstance.get(url);
  return res.data.data;
};

const downloadExcelReport = async (requestData: STRExcelExportParams) => {
  const queryParams = new URLSearchParams();
  Object.entries(requestData).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value.toString());
  });

  const url = `/redCash/api/v1.0/str-excel/download?${queryParams}`;
  const res = await collectionSetupAxiosInstance.get(url, blobConfig);
  return res.data;
};

const sendExcelEmail = async (requestData: STREmailParams) => {
  const url = `/redCash/api/v1.0/str-excel/send-email`;
  const res = await collectionSetupAxiosInstance.post(url, requestData);
  return res.data;
};

const sendDailySummaryEmail = async (requestData: {
  date: string;
  recipients: string[];
  includeDetailedBreakdown?: boolean;
  includeCharts?: boolean;
  customMessage?: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH';
  scheduleTime?: string; // ISO string for scheduling
}) => {
  const url = `/redCash/api/v1.0/str-summary/daily/send-email`;
  const res = await collectionSetupAxiosInstance.post(url, {
    date: requestData.date,
    recipients: requestData.recipients,
    includeDetailedBreakdown: requestData.includeDetailedBreakdown || false,
    includeCharts: requestData.includeCharts || true,
    customMessage: requestData.customMessage || '',
    priority: requestData.priority || 'NORMAL',
    scheduleTime: requestData.scheduleTime,
  });
  return res.data;
};

const strService = {
  getDashboardStats,
  getDailySummary,
  getWeeklySummary,
  getAllRules,
  getRule,
  updateRuleThresholds,
  toggleRule,
  getAlerts,
  hideAlert,
  showAlert,
  resolveAlert,
  bulkHideAlerts,
  hideAllAccountAlerts,
  hideRuleAlertsForAccount,
  getAlertStatistics,
  getAccountMonitoringStatus,
  searchAccountMonitoringByAgreementNo,
  disableAccountMonitoring,
  enableAccountMonitoring,
  disableAccountRules,
  enableAccountRules,
  unflagAccount,
  getAccountFlags,
  downloadExcelReport,
  sendExcelEmail,
  sendDailySummaryEmail,
};

export default strService;
